import React from "react";
import { Link } from "react-router-dom";
import Layout from "../../components/Layout";

const Home = () => {
    return (
        <Layout>
            <section className="hero">
                <div className="container">
                    {/* Top: eyebrow / headline / CTA  |  stack image */}
                    <div className="hero-grid">
                        <div className="hero-text">
                            <div className="eyebrow">
                                <span className="dot"></span> Payments, wallets, and digital value
                            </div>
                            <h1>Regulated infrastructure for wallets, stablecoins, and payments</h1>
                            <p className="lead">
                            Launch compliant wallet, fiat/crypto/stablecoin, card, and payment flows through one embedded platform.
                            </p>
                            <div className="actions">
                                <Link className="btn btn-primary" to="/contact">Contact Us</Link>
                                <Link className="btn btn-secondary" to="/solutions">Explore Solutions</Link>
                            </div>
                        </div>
                        <aside className="hero-stack-image">
                            <img src="/images/why-everest-stack.png" alt="Everest stack: who connects, integration surface, identity layer, account layer, settlement layer, rails, and settled value" />
                        </aside>
                    </div>

                    {/* Full-width summary + proof grid below both columns */}
                    <div className="hero-lower">
                        <div className="visual-summary">
                            <div className="summary-block">
                                <div className="summary-icon">
                                    <img src="/icons/solutions.svg" alt="Solutions icon" />
                                </div>
                                <div>
                                    <strong>Solutions</strong>
                                    <ul className="summary-list">
                                        <li>Fiat-to-crypto</li>
                                        <li>Crypto payments</li>
                                        <li>Fiat-denominated payment accounts</li>
                                        <li>Stablecoins</li>
                                    </ul>
                                </div>
                            </div>
                            <div className="summary-block">
                                <div className="summary-icon">
                                    <img src="/icons/packages.svg" alt="Packages icon" />
                                </div>
                                <div>
                                    <strong>Packages</strong>
                                    <ul className="summary-list">
                                        <li>APIs</li>
                                        <li>Widget / iFrame</li>
                                        <li>White-label EverWallet</li>
                                    </ul>
                                </div>
                            </div>
                            <div className="summary-block">
                                <div className="summary-icon">
                                    <img src="/icons/embedded-stack.svg" alt="Embedded stack icon" />
                                </div>
                                <div>
                                    <strong>Embedded stack</strong>
                                    <ul className="summary-list">
                                        <li>KYC / AML</li>
                                        <li>Transaction monitoring</li>
                                        <li>Custody (where licensed and applicable) + wallet infrastructure</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/*
              Current regulatory status (transitional). Once MiCA authorisation is granted,
              replace the statement below everywhere with:
              "Everest Network Ltd. is licensed by the Malta Financial Services Authority as a
              Crypto-Asset Service Provider under Regulation (EU) 2023/1114 on Markets in
              Crypto-Assets, as implemented in Malta by the Markets in Crypto-Assets Act."
            */}
            <section className="section section-tight">
                <div className="container">
                    <div className="notice-box">
                        <strong>Current regulatory status</strong>
                        <p style={{ marginTop: 8 }}>
                            Everest Network Ltd. is licensed by the Malta Financial Services Authority as a
                            Virtual Financial Asset Service Provider under the Virtual Financial Assets Act and
                            operates under applicable transitional arrangements pending authorisation under
                            Regulation (EU) 2023/1114 on Markets in Crypto-Assets (MiCA).
                        </p>
                    </div>
                </div>
            </section>

            <section className="section section-tight">
                <div className="container">
                    <div className="section-head">
                        <span className="kicker">Solutions</span>
                        <h2>Built for payment infrastructure</h2>
                        <p>Each solution is delivered on the same Everest infrastructure layer, combining onboarding, compliance controls, transaction monitoring, custody, and wallet services in a single platform.
                        </p>
                    </div>
                    <div className="grid-4">
                        <article className="feature-card">
                            <div className="icon-badge">
                                <img src="/icons/fiat-to-crypto.svg" alt="Fiat-to-crypto icon" />
                            </div>
                            <h3>Fiat-to-crypto</h3>
                            <p>Cards, APMs, and banking rails for wallet funding and purchase flows.</p>
                            <ul className="micro-list">
                                <li><strong>Feature:</strong> multi-rail funding</li>
                                <li><strong>Benefit:</strong> broader acceptance and potential for optimised routing costs, depending on usage</li>
                            </ul>
                        </article>
                        <article className="feature-card">
                            <div className="icon-badge">
                                <img src="/icons/crypto-payments.svg" alt="Crypto payments icon" />
                            </div>
                            <h3>Crypto payments</h3>
                            <p>Merchant acceptance, OTC support, and D2C payment flows.</p>
                            <ul className="micro-list">
                                <li><strong>Feature:</strong> crypto acceptance and settlement workflows</li>
                                <li><strong>Benefit:</strong> more payment choice and less manual handling</li>
                            </ul>
                            <p className="feature-risk-note" style={{ fontSize: "12px", marginTop: 8 }}>Crypto-asset services involve significant risk, including market volatility, liquidity risk, and the potential loss of value.</p>
                        </article>
                        <article className="feature-card">
                            <div className="icon-badge">
                                <img src="/icons/fiat.svg" alt="Fiat icon" />
                            </div>
                            <h3>Fiat</h3>
                            <p>vIBANs, e-wallets, and payment-account functionality, provided through licensed partners where applicable. Everest is not a bank and does not provide deposit-taking services.</p>
                            <ul className="micro-list">
                                <li><strong>Feature:</strong> named fiat accounts and wallet balances</li>
                                <li><strong>Benefit:</strong> stronger retention and repeat payment activity</li>
                            </ul>
                        </article>
                        <article className="feature-card">
                            <div className="icon-badge">
                                <img src="/icons/stabecoins.svg" alt="Stablecoins icon" />
                            </div>
                            <h3>Stablecoins</h3>
                            <p>Stablecoin-based payments and stablecoin infrastructure services (subject to market and issuer risks).</p>
                            <ul className="micro-list">
                                <li><strong>Feature:</strong> programmable value movement</li>
                                <li><strong>Benefit:</strong> faster settlement design and enhanced treasury management flexibility</li>
                            </ul>
                            <p className="feature-risk-note" style={{ fontSize: "12px", marginTop: 8 }}>Stablecoin services are subject to regulatory classification, issuer risk, and jurisdictional availability.</p>
                        </article>
                    </div>
                </div>
            </section>

            <section className="section section-tight">
                <div className="container">
                    <div className="section-head">
                        <h2>Why Everest</h2>
                        <p>Why merchants, fintechs, PSPs, acquirers, and platforms choose Everest.</p>
                    </div>
                    <div className="solution-list why-everest-grid">
                        <div className="mini-card">
                            <strong>One regulated stack</strong>
                            <span className="notice">Onboarding, wallet creation, custody controls, payment flows, stablecoin movement, monitoring, and reconciliation in one platform.</span>
                        </div>
                        <div className="mini-card">
                            <strong>Built for embedded distribution</strong>
                            <span className="notice">Deploy through APIs, hosted flows, iFrame widgets, or white-label EverWallet.</span>
                        </div>
                        <div className="mini-card">
                            <strong>Designed for regulated digital value</strong>
                            <span className="notice">Support fiat, crypto, stablecoin, and tokenized-value workflows with compliance and audit controls built into the transaction lifecycle.</span>
                        </div>
                        <div className="mini-card">
                            <strong>Real-time settlement layer</strong>
                            <span className="notice">Everest uses a permissioned ledger environment, EverChain, to support internal wallet transfers, tokenized balances of crypto and fiat, wrapped assets, compliance-logged transactions, and real-time reconciliation.</span>
                        </div>
                    </div>
                </div>
            </section>

            <section className="section">
                <div className="container">
                    <div className="container split-panel">
                        <div className="dark-band">
                            <span className="kicker">Packages</span>
                            <h3>Choose the delivery model</h3>
                            <p>Start with the package that matches your team today, then expand over time without replacing your underlying stack
                            </p>
                            <ul className="check-list">
                                <li><strong>API</strong> for direct integration and custom payment logic.</li>
                                <li><strong>Widget / iFrame</strong> for faster launch and lower build effort.</li>
                                <li><strong>White-label EverWallet</strong> for a branded wallet and payments front end.</li>
                            </ul>
                            <p style={{fontSize: "12px"}}>Services are provided subject to applicable regulatory permissions and may not be available in all jurisdictions. Everest Network Ltd. is licensed by the Malta Financial Services Authority as a Virtual Financial Asset Service Provider under the Virtual Financial Assets Act and operates under applicable transitional arrangements pending authorisation under Regulation (EU) 2023/1114 on Markets in Crypto-Assets (MiCA).
                            </p>
                            <div>
                                <Link className="btn btn-secondary" to="/solutions#packages">Compare packages</Link>
                            </div>
                        </div>
                        <div className="grid-1 stack">
                            <div className="card">
                                <div className="icon-badge">
                                    <img src="/icons/kyc-aml.svg" alt="KYC AML icon" />
                                </div>
                                <h3>KYC / AML</h3>
                                <p>Screening and onboarding tools integrated into each flow to support compliance processes.</p>
                            </div>
                            <div className="card">
                                <div className="icon-badge">
                                    <img src="/icons/transaction-monitoring.svg" alt="Transaction monitoring icon" />
                                </div>
                                <h3>Transaction monitoring</h3>
                                <p>Controls for payment and wallet activity.</p>
                            </div>
                            <div className="card">
                                <div className="icon-badge">
                                    <img src="/icons/custody-wallet-stack.svg" alt="Custody wallet stack icon" />
                                </div>
                                <h3>Custody (where licensed and applicable) + wallet stack</h3>
                                <p>Managed digital-asset infrastructure (where permitted) and account infrastructure.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* End homepage content */}
        </Layout>
    );
};

export default Home;
